import React, { Fragment, useEffect, useState } from "react";
import { Box, Checkbox, Chip, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import { Controller } from "react-hook-form";

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import { useTranslation } from "react-i18next";
import NumberFormatTheme from "../../../../shared/general/NumberFormatTheme";

import { getRoundNumber } from "../../../../../../utils"
import LeaveStepForm from "./leaveStepForm";

const LeaveTypeForm = (props) => {

  const { 
    index, 
    useHookForm, 
    handleRemoveLeaveType, 
    handleMoveUpLeaveType, 
    handleMoveDownLeaveType,
    groupLevelList,
    employeeTypeList,
  } = props;
  
  const { t } = useTranslation();

  const [idGroupLevelMap, setIdGroupLevelMap] = useState({});
  const [idEmployeeTypeMap, setIdEmployeeTypeMap] = useState({});

  useEffect(() => {
    const mapTmp = {}
    groupLevelList.map(item => {
      mapTmp[item.idGroupLevel] = item;
    })
    setIdGroupLevelMap(mapTmp)
  }, [groupLevelList])

  useEffect(() => {
    const mapTmp = {}
    employeeTypeList.map(item => {
      mapTmp[item.idEmployeeType] = item;
    })
    setIdEmployeeTypeMap(mapTmp)
  }, [employeeTypeList])

  const isLeaveStepWatch = useHookForm.watch(`leaveTypeList.${index}.isLeaveStep`);
  const canRequestAfterWatch = useHookForm.watch(`leaveTypeList.${index}.canRequestAfter`);
  const isProRateWatch = useHookForm.watch(`leaveTypeList.${index}.isProRate`);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Box className="header-leave-type">
              <Typography fontSize="18px" fontWeight="600">{t("EmployeeGroup")} {index + 1}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap="4px">
            <IconButton
              onClick={() => {handleMoveUpLeaveType(index)}}
              disabled={index === 0}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
            <IconButton
              onClick={() => {handleMoveDownLeaveType(index)}}
              disabled={index === useHookForm.getValues("leaveTypeList").length-1}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
            <IconButton
              onClick={() => {handleRemoveLeaveType(index, useHookForm.getValues(`leaveTypeList.${index}.idLeaveType`))}}
              disabled={useHookForm.getValues("leaveTypeList").length === 1}
            >
              <DeleteRoundedIcon />
            </IconButton>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("EmployeeType")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.idEmployeeTypeList`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              SelectProps={{
                multiple: true,
                renderValue: (select) => (
                  <Box>
                    {select.map((value) => (
                      <Chip 
                        key={value} 
                        label={
                          value === "all"? t("All")
                          : idEmployeeTypeMap[value]
                            ? `${idEmployeeTypeMap[value].employeeTypeName} (${idEmployeeTypeMap[value].companyName})`
                            : value
                        }
                      />
                    ))}
                  </Box>
                )
              }}
              onChange={event => {
                if(event.target.value.length > 0 && event.target.value[event.target.value.length - 1] !== "all"){
                  field.onChange(event.target.value.filter(x => x !== "all"))
                } else if (event.target.value.includes("all")) {
                  field.onChange(["all"])
                } else {
                  field.onChange(event.target.value)
                }
              }}
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="all">{t("All")}</MenuItem>
              {employeeTypeList.map(item => (
                <MenuItem key={item.idEmployeeType} value={item.idEmployeeType}>
                  <Box>
                    <Typography fontSize="14px" color="text.secondary">{item.companyName}</Typography>
                    <Typography>{item.employeeTypeName}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("PersonalLevel")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.idPersonnelLevelGroupList`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              SelectProps={{
                multiple: true,
                renderValue: (select) => (
                  <Box>
                    {select.map((value) => (
                      <Chip 
                        key={value} 
                        label={
                          value === "all"? t("All")
                          : idGroupLevelMap[value]
                            ? `${idGroupLevelMap[value].groupLevelName} (${idGroupLevelMap[value].companyName})`
                            : value
                        }
                      />
                    ))}
                  </Box>
                )
              }}
              onChange={event => {
                if(event.target.value.length > 0 && event.target.value[event.target.value.length - 1] !== "all"){
                  field.onChange(event.target.value.filter(x => x !== "all"))
                } else if (event.target.value.includes("all")) {
                  field.onChange(["all"])
                } else {
                  field.onChange(event.target.value)
                }
              }}
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="all">{t("All")}</MenuItem>
              {groupLevelList.map(item => (
                <MenuItem key={item.idGroupLevel} value={item.idGroupLevel}>
                  <Box>
                    <Typography fontSize="14px" color="text.secondary">{item.companyName}</Typography>
                    <Typography>{item.groupLevelName}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("IsPaid")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.isPaid`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              multiple
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="1">{t("PaidLeave")}</MenuItem>
              <MenuItem value="0">{t("UnpaidLeave")}</MenuItem>
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("CanRequestLeaveAfter")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.canRequestAfter`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="0">{t("FirstDayOfService")}</MenuItem>
              <MenuItem value="60">{`${t("YearOfService")} 60 ${t("Days")}`}</MenuItem>
              <MenuItem value="90">{`${t("YearOfService")} 90 ${t("Days")}`}</MenuItem>
              <MenuItem value="120">{`${t("YearOfService")} 120 ${t("Days")}`}</MenuItem>
              <MenuItem value="180">{`${t("YearOfService")} 180 ${t("Days")}`}</MenuItem>
              <MenuItem value="240">{`${t("YearOfService")} 240 ${t("Days")}`}</MenuItem>
              <MenuItem value="365">{`${t("YearOfService")} 1 ${t("Year")}`}</MenuItem>
              <MenuItem value="730">{`${t("YearOfService")} 2 ${t("Year")}`}</MenuItem>
              <MenuItem value="1095">{`${t("YearOfService")} 3 ${t("Year")}`}</MenuItem>
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("Gender")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.gender`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="all">{t("All")}</MenuItem>
              <MenuItem value="ชาย">{t("Male")}</MenuItem>
              <MenuItem value="หญิง">{t("Female")}</MenuItem>
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("MinimumLeaveAllowed")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.minLeave`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="100">{t("FullDay")}</MenuItem>
              <MenuItem value="50">{t("HalfDay")}</MenuItem>
              <MenuItem value="10">{`1 ${t("Hours")}`}</MenuItem>
              <MenuItem value="5">{t("HalfHour")}</MenuItem>
            </TextFieldTheme>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("Leave.AdvanceAtLeast")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.dayInAdvance`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              inputProps={{
                placeholder: t("Leave.Unlimited"),
                allowNegative: false,
                value: field.value,
                onValueChange: (values) => {
                  const { value } = values;
                  field.onChange(value)
                },
                decimalScale: 0,
                thousandSeparator: ",",
                suffix: ` ${t("Days")}`
              }}
              onChange={()=>{}}
              onBlur={() => {
                if(field.value && !isNaN(field.value) && field.value > 365){
                  field.onChange(365)
                }
              }}
              InputProps={{
                inputComponent: NumberFormatTheme,
              }}
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("TypeOfLeaveDays")}</Typography>
        <Controller
          name={`leaveTypeList.${index}.isLeaveStep`}
          control={useHookForm.control}
          render={({ field, fieldState }) => (
            <TextFieldTheme
              {...field}
              select
              onChange={(event) => {
                field.onChange(event.target.value);
                useHookForm.setValue(`leaveTypeList.${index}.leaveValue`, "")
                useHookForm.setValue(`leaveTypeList.${index}.carryValue`, "")
              }}
              helperText={fieldState.error? fieldState.error.message: null}
              error={fieldState.error? true: false}
            >
              <MenuItem value="0">{t("Fixed")}</MenuItem>
              <MenuItem value="1">{t("Step")}</MenuItem>
            </TextFieldTheme>
          )}
        />
      </Grid>

      {canRequestAfterWatch < 365 && (
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontSize="14px" fontWeight="500">{t("CalProRateLessThanOneYear")}</Typography>
            <Controller
              name={`leaveTypeList.${index}.isProRate`}
              control={useHookForm.control}
              render={({ field, fieldState }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                />
              )}
            />
          </Box>
        </Grid>
      )}

      {Boolean(isProRateWatch) === true && (
        <Grid item xs={12}>
          <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("RoundsDecimalIsGreater")}</Typography>
          <Controller
            name={`leaveTypeList.${index}.proRateRound`}
            control={useHookForm.control}
            render={({ field, fieldState }) => (
              <TextFieldTheme
                {...field}
                select
                helperText={fieldState.error? fieldState.error.message: null}
                error={fieldState.error? true: false}
              >
                <MenuItem value="1">{t("ThereIsNoRounding")}</MenuItem>
                <MenuItem value="0.5">{`${t("RoundWhenGreaterThan")} 0.5`}</MenuItem>
                <MenuItem value="0">{t("RoundUpInAllCases")}</MenuItem>
              </TextFieldTheme>
            )}
          />
        </Grid>
      )}

      {Number(isLeaveStepWatch) === 1? (
        <Fragment>
          <LeaveStepForm
            useHookForm={useHookForm}
            indexLeaveType={index}
          />
        </Fragment>
      ): (
        <Fragment>
          <Grid item xs={12} md={6}>
            <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("TotalLeaveDays")}</Typography>
            <Controller
              name={`leaveTypeList.${index}.leaveValue`}
              control={useHookForm.control}
              render={({ field, fieldState }) => (
                <TextFieldTheme
                  {...field}
                  inputProps={{
                    placeholder: t("Leave.Unlimited"),
                    allowNegative: false,
                    value: field.value,
                    onValueChange: (values) => {
                      const { value } = values;
                      field.onChange(value)
                    },
                    decimalScale: 0,
                    thousandSeparator: ",",
                    suffix: ` ${t("Days")}`
                  }}
                  onChange={()=>{}}
                  InputProps={{
                    inputComponent: NumberFormatTheme,
                  }}
                  helperText={fieldState.error? fieldState.error.message: null}
                  error={fieldState.error? true: false}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography fontSize="14px" fontWeight="500" paddingBottom="4px">{t("CannotCarryOverLeaveNextRound")}</Typography>
            <Controller
              name={`leaveTypeList.${index}.carryValue`}
              control={useHookForm.control}
              render={({ field, fieldState }) => (
                <TextFieldTheme
                  {...field}
                  inputProps={{
                    placeholder: t("Leave.Unlimited"),
                    allowNegative: false,
                    value: field.value,
                    onValueChange: (values) => {
                      const { value } = values;
                      field.onChange(value)
                    },
                    decimalScale: 0,
                    thousandSeparator: ",",
                    suffix: ` ${t("Days")}`
                  }}
                  onChange={()=>{}}
                  InputProps={{
                    inputComponent: NumberFormatTheme,
                  }}
                  helperText={fieldState.error? fieldState.error.message: null}
                  error={fieldState.error? true: false}
                />
              )}
            />
          </Grid>
        </Fragment>
      )}
    </Grid>
  )
}

export default LeaveTypeForm;